import { ApolloError } from '@apollo/client';
import { ErrorPage, LoadingPage, Modal, ModalForm, TControlledFormComponentProps, TControlledFormProps } from '@chocolate-soup-inc/cs-frontend-components';
import { joiResolver } from '@hookform/resolvers/joi';
import { useCallback, useMemo } from 'react';
import { generatePath, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useQueryListAllCompaniesOptions } from '../../entities/companies/queries';
import { getGiftPackageFormFields } from '../../entities/giftPackages/formFields';
import { getGiftPackageSchema } from '../../entities/giftPackages/schema';
import { useQueryAllProducts } from '../../entities/products/queries';
import { TCreateGiftPackageMutationInput, useCreateGiftPackageMutation } from '../../generated/graphql';

export const GiftPackageCreate = () => {
  const navigate = useNavigate();

  const { data: productsData, loading: productsLoading, error: productsError } = useQueryAllProducts();
  const { data: companiesData, loading: companiesLoading, error: companiesError } = useQueryListAllCompaniesOptions();
  const [createGiftPackage] = useCreateGiftPackageMutation();

  const companies = useMemo(() => {
    return companiesData?.map((c) => ({ label: c.name, value: c.id }));
  }, [companiesData]);

  const formProducts = useMemo(() => {
    return productsData.map((p) => ({
      label: `${p.description} [${p.quantity}u, $${p.value} CAD, ${p.hsCode}]`,
      value: p.id
    }));
  }, [productsData]);

  const closeModal = useCallback(() => {
    navigate(generatePath('../'));
  }, [navigate]);

  const onInvalidSubmit = useCallback<Exclude<TControlledFormProps<TCreateGiftPackageMutationInput>['onInvalidSubmit'], undefined>>((errors: Record<string, any>) => {
    console.error(errors);
    const submitError = errors[''];
    if (submitError) toast.error(submitError.message);
  }, []);

  const onSubmit = useCallback<TControlledFormProps<TCreateGiftPackageMutationInput>['onValidSubmit']>(
    (formData) => {
      const input = {
        ...formData,
        isDefault: formData.isDefault ? 1 : 0
      };

      return createGiftPackage({
        variables: {
          input
        }
      })
        .then(() => {
          toast.success('Successfully created the Gift Package.');
          closeModal();
        })
        .catch((error: ApolloError) => {
          console.log('error.', error);
          toast.error('There was an error creating the Gift Package.');
          if (error instanceof ApolloError) error.graphQLErrors.forEach((err) => toast.error(err.message));
        });
    },
    [closeModal, createGiftPackage]
  );

  if (productsLoading && companiesLoading)
    return (
      <Modal closeModal={closeModal} headline={`Create New Gift Package/Kit`}>
        <LoadingPage />
      </Modal>
    );

  if (productsError && companiesError) {
    toast.error('An error has ocurred, check the console for details. (query Apollo ERROR)');
    //if (productsError.message) toast.error(productsError.message);
    if (!productsData || companiesData)
      return (
        <Modal closeModal={closeModal} headline={`Create New Gift Package/Kit`}>
          <ErrorPage error={productsError || companiesError} />
        </Modal>
      );
  }

  return (
    <ModalForm
      headline={`Create New Gift Package/Kit`}
      size='large'
      closeModal={closeModal}
      controlledFormProps={{
        fields: getGiftPackageFormFields(formProducts, companies) as TControlledFormComponentProps<TCreateGiftPackageMutationInput>[],
        formProps: {
          resolver: joiResolver(getGiftPackageSchema(), {
            convert: true,
            abortEarly: false,
            stripUnknown: false
          })
        },
        onValidSubmit: onSubmit,
        onInvalidSubmit: onInvalidSubmit
      }}
    />
  );
};
